/**
 * UpSurv 2.0 — structured logging.
 *
 * One line = one JSON object with a stable `event` name. Cloudflare's log tail
 * and Logpush both treat a JSON `console` argument as structured data, so this
 * is what makes "a filtered text must never fail silently" (DECISIONS D-005)
 * actually greppable months later instead of a wall of prose.
 *
 * The sink is injected everywhere it is used. Production wires
 * `consoleLogSink`; tests wire a collector and assert on the lines, which is
 * how the SMS observability requirement is verified without sending anything.
 */

export type LogLevel = 'info' | 'warn' | 'error';

/**
 * `event` is a dotted, stable name — `sms.attempt`, `sms.response`,
 * `notify.dispatch`. Never interpolate values into it; put them in fields, or
 * the name stops being groupable.
 */
export interface LogLine {
  level: LogLevel;
  event: string;
  [field: string]: unknown;
}

export type LogSink = (line: LogLine) => void;

/**
 * The default sink. `console` is a Web API present in both Workers and the
 * browser — nothing here reaches for a Node logger.
 */
export const consoleLogSink: LogSink = (line) => {
  const serialized = JSON.stringify(line);
  if (line.level === 'error') console.error(serialized);
  else if (line.level === 'warn') console.warn(serialized);
  else console.log(serialized);
};

/** Drops everything. For call sites that must not log (or tests that don't care). */
export const nullLogSink: LogSink = () => {};
